import React from "react";
import styled from "styled-components";

/**
 * PillTabs - a wrapping row of pill-shaped buttons for switching between a
 * handful of views (replaces long "Select ..." dropdowns).
 *
 * Props:
 *  - options:   [{ value, label }]
 *  - value:     currently selected option value
 *  - onChange:  (value) => void
 *  - ariaLabel: accessible name for the group
 *  - margin:    optional outer margin
 */

const ACTIVE_BG = "rgba(45, 35, 66, 0.8)";
const BORDER = "#D6D6E7";
const TEXT = "#36395A";

const Group = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	margin: ${(props) => props.margin};
`;

const Pill = styled.button`
	appearance: none;
	font-family: Raleway, sans-serif;
	font-size: 0.95rem;
	font-weight: 500;
	line-height: 1;
	white-space: nowrap;
	cursor: pointer;
	padding: 0.55rem 1.1rem;
	border-radius: 8px;
	border: 1px solid ${(props) => (props.$active ? ACTIVE_BG : BORDER)};
	background: ${(props) => (props.$active ? ACTIVE_BG : "#fff")};
	color: ${(props) => (props.$active ? "#fff" : TEXT)};
	transition: background-color 0.15s, border-color 0.15s, color 0.15s;
	-webkit-tap-highlight-color: transparent;
	touch-action: manipulation;

	&:hover:not([aria-pressed="true"]) {
		border-color: ${ACTIVE_BG};
		color: ${ACTIVE_BG};
	}

	&:focus-visible {
		outline: 2px solid ${ACTIVE_BG};
		outline-offset: 2px;
	}
`;

const PillTabs = ({ options = [], value, onChange, ariaLabel, margin = "0" }) => (
	<Group role="group" aria-label={ariaLabel} margin={margin}>
		{options.map((option) => (
			<Pill
				key={option.value}
				type="button"
				$active={option.value === value}
				aria-pressed={option.value === value}
				onClick={() => onChange(option.value)}>
				{option.label}
			</Pill>
		))}
	</Group>
);

export default PillTabs;
